<template>
  <div class="regras-listagem">
    <Loading :isActive="loading && !jaCarregou" />

    <ModalRegraCashback :item="currentItem" :close="closeEdit" />

    <div class="regras-listagem__header">
      <CButton color="primary" @click="openCreate">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Nova regra
      </CButton>
    </div>

    <div class="regras-listagem__filtros">
      <CRow>
        <CCol md="8">
          <div class="regras-listagem__busca">
            <input
              class="form-control"
              type="text"
              v-model="filtros.termo"
              placeholder="Buscar por nome ou descrição..."
              aria-label="Buscar regra por nome ou descrição"
              @input="loadRegrasComDebounce()"
            />
            <fa-icon v-if="loading && jaCarregou" icon="spinner" spin class="regras-listagem__busca-indicador" />
          </div>
        </CCol>
        <CCol md="4">
          <select class="form-control" v-model="filtros.ativo" aria-label="Situação da regra" @change="loadRegras()">
            <option value="">Situação: Todas</option>
            <option value="1">Ativas</option>
            <option value="0">Inativas</option>
          </select>
        </CCol>
      </CRow>
    </div>

    <div :class="['regras-listagem__table-wrapper', { 'regras-listagem__table-wrapper--atualizando': loading && jaCarregou }]">
      <table class="regras-listagem__table">
        <thead>
          <tr>
            <th class="regras-listagem__col-id">ID</th>
            <th>Nome</th>
            <th class="cashback-listagem__col-prioridade">Prioridade</th>
            <th>Faixas</th>
            <th>Canais</th>
            <th class="regras-listagem__col-vigencia">Vigência</th>
            <th class="regras-listagem__col-ativo">Ativa</th>
            <th class="regras-listagem__col-acoes"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="erroCarga">
            <td colspan="8" class="regras-listagem__empty">
              <div class="regras-listagem__erro">Não foi possível carregar as regras: {{ erroCarga }}</div>
              <CButton color="primary" variant="outline" size="sm" @click="loadRegras(paginaAtual)">Tentar novamente</CButton>
            </td>
          </tr>
          <tr v-else-if="regras.length === 0">
            <td colspan="8" class="regras-listagem__empty">Nenhuma regra de cashback cadastrada</td>
          </tr>
          <tr v-for="regra in regras" :key="regra.id" class="regras-listagem__row">
            <td class="regras-listagem__col-id">{{ regra.id }}</td>
            <td><strong>{{ regra.nome }}</strong></td>
            <td class="cashback-listagem__col-prioridade">{{ regra.prioridade }}</td>
            <td>
              <div
                v-if="(regra.faixas || []).length"
                class="cashback-listagem__faixas"
                v-c-tooltip="{ content: detalheFaixas(regra), html: true, placement: 'top', boundaries: 'window' }"
              >
                <span class="cashback-listagem__faixas-retorno">{{ retornoFaixas(regra) }}</span>
                <span class="cashback-listagem__faixas-apoio">{{ apoioFaixas(regra) }}</span>
              </div>
              <span v-else class="cashback-listagem__faixas-apoio">—</span>
            </td>
            <td>
              <span v-for="canal in regra.canais" :key="canal" class="regras-badge regras-badge--canal">
                <img v-if="imagemCanal(canal)" :src="imagemCanal(canal)" alt="" class="regras-badge__logo" />
                {{ labelCanal(canal) }}
              </span>
            </td>
            <td class="regras-listagem__col-vigencia">{{ vigencia(regra) }}</td>
            <td class="regras-listagem__col-ativo">
              <span :class="['regras-badge', regra.ativo ? 'regras-badge--ativo' : 'regras-badge--inativo']">
                {{ regra.ativo ? "Sim" : "Não" }}
              </span>
            </td>
            <td class="regras-listagem__col-acoes">
              <CButton
                size="sm"
                color="secondary"
                class="regras-listagem__botao-acao"
                title="Duplicar"
                :aria-label="`Duplicar a regra ${regra.nome}`"
                @click="duplicar(regra)"
              >
                <fa-icon icon="clone" />
              </CButton>
              <CButton
                size="sm"
                color="primary"
                class="regras-listagem__botao-acao"
                title="Alterar"
                :aria-label="`Alterar a regra ${regra.nome}`"
                @click="editar(regra)"
              >
                <fa-icon icon="pencil-alt" />
              </CButton>
              <CButton
                size="sm"
                color="danger"
                class="regras-listagem__botao-acao"
                title="Excluir"
                :aria-label="`Excluir a regra ${regra.nome}`"
                @click="excluir(regra)"
              >
                <fa-icon icon="trash-alt" />
              </CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="regras-listagem__pagination">
      <pagination :data="paginator" :limit="5" @pagination-change-page="loadRegras" />
    </div>
  </div>
</template>

<script>
import clone from "just-clone";
import _debounce from "lodash/debounce";
import { get, del } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import ModalRegraCashback from "./ModalRegraCashback/ModalRegraCashback.vue";
import { imagemCanal } from "./canais";
import {
  DEBOUNCE_BUSCA_MS,
  ITENS_POR_PAGINA,
  formatData,
  formatMoney,
  formatPercentual,
  mensagemErroApi,
  nomeCopia,
  novaRegra,
  numero,
  ordenarFaixas,
  rotulosPorChave,
  tetoDaFaixa,
} from "./helpers";

export default {
  name: "cashback-regras",
  components: { Loading, ModalRegraCashback },

  data() {
    return {
      loading: false,
      jaCarregou: false,
      erroCarga: null,
      regras: [],
      paginator: {},
      rotulosCanais: {},
      currentItem: novaRegra(),
      filtros: { termo: "", ativo: "" },
    };
  },

  computed: {
    paginaAtual() {
      return this.paginator.current_page || 1;
    },
  },

  methods: {
    imagemCanal,

    loadRegras(page = 1) {
      this.loading = true;
      this.erroCarga = null;
      const sequencia = (this.sequenciaCarga = (this.sequenciaCarga || 0) + 1);
      const params = new URLSearchParams({ page, take: ITENS_POR_PAGINA, q: this.filtros.termo, ativo: this.filtros.ativo });

      return get(`/admin/cashback/regras?${params}`)
        .then((json) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = json.itens || [];
          this.paginator = json.paginator || {};
        })
        .catch((err) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = [];
          this.erroCarga = mensagemErroApi(err);
        })
        .finally(() => {
          if (sequencia !== this.sequenciaCarga) return;

          this.loading = false;
          this.jaCarregou = true;
        });
    },

    loadRegrasComDebounce: _debounce(function () {
      this.loadRegras();
    }, DEBOUNCE_BUSCA_MS),

    loadRotulosCanais() {
      return get("/admin/cashback/regras/opcoes")
        .then((json) => {
          this.rotulosCanais = rotulosPorChave(json && json.canais);
        })
        .catch(() => {
          this.rotulosCanais = {};
        });
    },

    openCreate() {
      this.currentItem = novaRegra();
      this.$modal.show("ModalRegraCashback");
    },

    editar(regra) {
      this.currentItem = clone(regra);
      this.$modal.show("ModalRegraCashback");
    },

    duplicar(regra) {
      const copia = clone(regra);
      copia.id = null;
      copia.ativo = false;
      copia.nome = nomeCopia(copia.nome);
      copia.faixas = (copia.faixas || []).map((faixa) => ({ ...faixa, id: null }));
      copia.restricoes = (copia.restricoes || []).map((restricao) => ({ ...restricao, id: null }));
      this.currentItem = copia;
      this.$modal.show("ModalRegraCashback");
    },

    closeEdit(salvou) {
      this.$modal.hide("ModalRegraCashback");

      if (salvou) this.loadRegras(this.paginaAtual);
    },

    excluir(regra) {
      this.$swal({
        title: "Excluir a regra de cashback?",
        html: "<p>Pedidos novos deixam de gerar cashback por esta regra. O cashback já confirmado na carteira dos clientes não é alterado.</p>",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (!res.value) return;

          this.loading = true;
          del(`/admin/cashback/regras/${regra.id}`)
            .then(() => this.loadRegras(this.regras.length === 1 && this.paginaAtual > 1 ? this.paginaAtual - 1 : this.paginaAtual))
            .catch((err) => this.$swal({ title: "Falha ao excluir", text: mensagemErroApi(err), icon: "error" }))
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(() => {});
    },

    labelCanal(canal) {
      return this.rotulosCanais[canal] || canal;
    },

    retornoFaixas(regra) {
      const percentuais = (regra.faixas || []).map((faixa) => numero(faixa.percentual)).filter((valor) => valor > 0);

      if (!percentuais.length) return formatPercentual(0);

      const menor = Math.min(...percentuais);
      const maior = Math.max(...percentuais);

      return menor === maior ? formatPercentual(menor) : `${formatPercentual(menor)} a ${formatPercentual(maior)}`;
    },

    apoioFaixas(regra) {
      const faixas = ordenarFaixas(regra.faixas);
      const quantidade = faixas.length === 1 ? "1 faixa" : `${faixas.length} faixas`;

      return `${quantidade} · a partir de ${formatMoney(faixas[0].valorMinimo)}`;
    },

    detalheFaixas(regra) {
      return ordenarFaixas(regra.faixas)
        .map((faixa) => {
          const teto = tetoDaFaixa(faixa);
          const intervalo = teto !== null ? `${formatMoney(faixa.valorMinimo)} a ${formatMoney(teto)}` : `A partir de ${formatMoney(faixa.valorMinimo)}`;
          const retorno = numero(faixa.percentual) > 0 ? formatPercentual(faixa.percentual) : "sem cashback";

          return `<div class="cashback-listagem__faixa-detalhe">${intervalo} → <strong>${retorno}</strong></div>`;
        })
        .join("");
    },

    vigencia(regra) {
      if (!regra.dataInicio && !regra.dataFim) return "Sem prazo";

      return `${formatData(regra.dataInicio, "…")} até ${formatData(regra.dataFim, "…")}`;
    },
  },

  beforeMount() {
    this.loadRegras();
    this.loadRotulosCanais();
  },
};
</script>

<style scoped src="@/components/regras/regras-listagem.css"></style>

<style scoped>
.cashback-listagem__faixas {
  display: inline-flex;
  flex-direction: column;
  cursor: help;
}

.cashback-listagem__faixas-retorno {
  font-weight: 600;
  color: #101828;
}

.cashback-listagem__faixas-apoio {
  font-size: 11.5px;
  color: #98a2b3;
}

.cashback-listagem__col-prioridade { width: 90px; text-align: center; }
</style>
